import { useState } from "react";
import {
  useListGiftRegistries,
  useCreateGiftRegistry,
  useUpdateGiftRegistry,
  useDeleteGiftRegistry,
  useGetGiftRegistry,
  useAddGiftRegistryItem,
  useUpdateGiftRegistryItem,
  useDeleteGiftRegistryItem,
  useListProducts,
  getListGiftRegistriesQueryKey,
  getGetGiftRegistryQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2, Plus, Edit, Trash2, Gift, Eye, Copy, Check, Search, Package } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/currency";

const EVENT_TYPES = ["Wedding", "Birthday", "Baby Shower", "Anniversary", "Engagement", "Graduation", "House Warming", "Other"];

const registrySchema = z.object({
  registrantName: z.string().min(1, "Name is required"),
  partnerName: z.string().optional(),
  eventType: z.string().min(1, "Event type is required"),
  eventDate: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  notes: z.string().optional(),
});

type RegistryFormValues = z.infer<typeof registrySchema>;

const itemSchema = z.object({
  productId: z.coerce.number().min(1, "Select a product"),
  quantityRequested: z.coerce.number().min(1),
  notes: z.string().optional(),
});

type ItemFormValues = z.infer<typeof itemSchema>;

function statusColor(status: string) {
  if (status === "fulfilled") return "bg-green-100 text-green-700 border-green-200";
  if (status === "cancelled") return "bg-red-100 text-red-700 border-red-200";
  return "bg-blue-100 text-blue-700 border-blue-200";
}

function RegistryForm({
  form,
  onSubmit,
  isPending,
}: {
  form: UseFormReturn<RegistryFormValues>;
  onSubmit: (data: RegistryFormValues) => void;
  isPending: boolean;
}) {
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField control={form.control} name="registrantName" render={({ field }) => (
            <FormItem>
              <FormLabel>Registrant Name *</FormLabel>
              <FormControl><Input autoFocus placeholder="e.g. Amara Obi" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="partnerName" render={({ field }) => (
            <FormItem>
              <FormLabel>Partner / Co-registrant</FormLabel>
              <FormControl><Input placeholder="e.g. Chidi Obi" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="eventType" render={({ field }) => (
            <FormItem>
              <FormLabel>Event Type *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl><SelectTrigger><SelectValue placeholder="Select event" /></SelectTrigger></FormControl>
                <SelectContent>
                  {EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="eventDate" render={({ field }) => (
            <FormItem>
              <FormLabel>Event Date</FormLabel>
              <FormControl><Input type="date" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="phone" render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl><Input placeholder="e.g. 08012345678" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="email" render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl><Input type="email" placeholder="optional" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
        </div>
        <FormField control={form.control} name="notes" render={({ field }) => (
          <FormItem>
            <FormLabel>Notes</FormLabel>
            <FormControl><Input placeholder="Any special notes..." {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />
        <Button type="submit" className="w-full" disabled={isPending}>Save Registry</Button>
      </form>
    </Form>
  );
}

function AddItemForm({
  form,
  onSubmit,
  isPending,
}: {
  form: UseFormReturn<ItemFormValues>;
  onSubmit: (data: ItemFormValues) => void;
  isPending: boolean;
}) {
  const { data: products } = useListProducts({});
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex items-end gap-3">
        <FormField control={form.control} name="productId" render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel>Product</FormLabel>
            <Select onValueChange={field.onChange} value={String(field.value || "")}>
              <FormControl><SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger></FormControl>
              <SelectContent>
                {products?.map(p => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}{p.sku ? ` (${p.sku})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )} />
        <FormField control={form.control} name="quantityRequested" render={({ field }) => (
          <FormItem className="w-24">
            <FormLabel>Qty</FormLabel>
            <FormControl><Input type="number" min={1} {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />
        <FormField control={form.control} name="notes" render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel>Notes</FormLabel>
            <FormControl><Input placeholder="Size, colour..." {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />
        <Button type="submit" disabled={isPending} className="mb-0.5">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </form>
    </Form>
  );
}

function RegistryDetailModal({ registryId, onClose }: { registryId: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: registry, isLoading } = useGetGiftRegistry(registryId);
  const addItem = useAddGiftRegistryItem();
  const updateItem = useUpdateGiftRegistryItem();
  const deleteItem = useDeleteGiftRegistryItem();
  const updateRegistry = useUpdateGiftRegistry();
  const [copied, setCopied] = useState(false);

  const itemForm = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    defaultValues: { productId: 0, quantityRequested: 1, notes: "" },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetGiftRegistryQueryKey(registryId) });
    queryClient.invalidateQueries({ queryKey: getListGiftRegistriesQueryKey() });
  };

  const handleAddItem = (data: ItemFormValues) => {
    addItem.mutate({ id: registryId, data: { productId: data.productId, quantityRequested: data.quantityRequested, notes: data.notes } }, {
      onSuccess: () => { invalidate(); itemForm.reset({ productId: 0, quantityRequested: 1, notes: "" }); },
      onError: () => toast({ title: "Failed to add item", variant: "destructive" }),
    });
  };

  const handleFulfill = (itemId: number, current: number, requested: number) => {
    const next = Math.min(current + 1, requested);
    updateItem.mutate({ id: registryId, itemId, data: { quantityFulfilled: next } }, {
      onSuccess: invalidate,
      onError: () => toast({ title: "Failed to update item", variant: "destructive" }),
    });
  };

  const handleUnfulfill = (itemId: number) => {
    updateItem.mutate({ id: registryId, itemId, data: { quantityFulfilled: 0 } }, {
      onSuccess: invalidate,
      onError: () => toast({ title: "Failed to update item", variant: "destructive" }),
    });
  };

  const handleRemoveItem = (itemId: number) => {
    if (confirm("Remove this item from the registry?")) {
      deleteItem.mutate({ id: registryId, itemId }, {
        onSuccess: invalidate,
        onError: () => toast({ title: "Failed to remove item", variant: "destructive" }),
      });
    }
  };

  const handleStatusChange = (status: string) => {
    updateRegistry.mutate({ id: registryId, data: { registrantName: registry!.registrantName, eventType: registry!.eventType, status } }, {
      onSuccess: invalidate,
      onError: () => toast({ title: "Failed to update status", variant: "destructive" }),
    });
  };

  const copyCode = () => {
    if (registry) {
      navigator.clipboard.writeText(registry.registryCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Code copied", description: registry.registryCode });
    }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>
  );

  if (!registry) return null;

  const fulfilled = registry.items?.filter(i => i.quantityFulfilled >= i.quantityRequested).length ?? 0;
  const total = registry.items?.length ?? 0;

  return (
    <div className="space-y-5">
      {/* Header info */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-lg">{registry.registrantName}{registry.partnerName ? ` & ${registry.partnerName}` : ""}</h3>
            <Badge variant="outline" className={statusColor(registry.status)}>{registry.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {registry.eventType}{registry.eventDate ? ` · ${format(new Date(registry.eventDate), "d MMM yyyy")}` : ""}
            {registry.phone ? ` · ${registry.phone}` : ""}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{fulfilled}/{total} items fulfilled</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Registry Code</p>
            <p className="font-mono font-bold text-sm">{registry.registryCode}</p>
          </div>
          <Button variant="outline" size="icon" onClick={copyCode}>
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Status change */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Status:</span>
        <Select value={registry.status} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-36 h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Items table */}
      {(registry.items?.length ?? 0) > 0 ? (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Price</TableHead>
                <TableHead className="text-center">Wanted</TableHead>
                <TableHead className="text-center">Fulfilled</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registry.items?.map(item => {
                const done = item.quantityFulfilled >= item.quantityRequested;
                return (
                  <TableRow key={item.id} className={done ? "opacity-60" : ""}>
                    <TableCell className="font-medium">{item.productName}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{item.productSku || "—"}</TableCell>
                    <TableCell>{formatCurrency(item.price)}</TableCell>
                    <TableCell className="text-center">{item.quantityRequested}</TableCell>
                    <TableCell className="text-center">
                      <span className={`font-semibold ${done ? "text-green-600" : ""}`}>
                        {item.quantityFulfilled}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{item.notes || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {!done ? (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-green-700"
                            onClick={() => handleFulfill(item.id, item.quantityFulfilled, item.quantityRequested)}>
                            <Check className="w-3 h-3 mr-1" /> Gift
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground"
                            onClick={() => handleUnfulfill(item.id)}>
                            Undo
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleRemoveItem(item.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="border rounded-md py-10 flex flex-col items-center gap-2 text-center">
          <Package className="w-8 h-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No items added yet</p>
        </div>
      )}

      {/* Add item form */}
      <div className="border-t pt-4">
        <p className="text-sm font-medium mb-3">Add Item to Registry</p>
        <AddItemForm form={itemForm} onSubmit={handleAddItem} isPending={addItem.isPending} />
      </div>
    </div>
  );
}

export default function GiftRegistryPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterEvent, setFilterEvent] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [lookupCode, setLookupCode] = useState("");

  const { toast } = useToast();
  const { data: registries, isLoading } = useListGiftRegistries({
    search: search || undefined,
    status: filterStatus !== "all" ? filterStatus : undefined,
    eventType: filterEvent !== "all" ? filterEvent : undefined,
  });

  const createRegistry = useCreateGiftRegistry();
  const updateRegistry = useUpdateGiftRegistry();
  const deleteRegistry = useDeleteGiftRegistry();

  const createForm = useForm<RegistryFormValues>({
    resolver: zodResolver(registrySchema),
    defaultValues: { registrantName: "", partnerName: "", eventType: "", eventDate: "", phone: "", email: "", notes: "" },
  });

  const editForm = useForm<RegistryFormValues>({
    resolver: zodResolver(registrySchema),
    defaultValues: { registrantName: "", partnerName: "", eventType: "", eventDate: "", phone: "", email: "", notes: "" },
  });

  const handleCreate = (data: RegistryFormValues) => {
    createRegistry.mutate({ data }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListGiftRegistriesQueryKey() });
        setIsCreateOpen(false);
        createForm.reset();
        toast({ title: "Registry created", description: "Registry code has been generated." });
      },
      onError: () => toast({ title: "Failed to create registry", variant: "destructive" }),
    });
  };

  const handleEdit = (r: any) => {
    setEditingId(r.id);
    editForm.reset({
      registrantName: r.registrantName,
      partnerName: r.partnerName || "",
      eventType: r.eventType,
      eventDate: r.eventDate || "",
      phone: r.phone || "",
      email: r.email || "",
      notes: r.notes || "",
    });
  };

  const handleUpdate = (data: RegistryFormValues) => {
    if (!editingId) return;
    updateRegistry.mutate({ id: editingId, data }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListGiftRegistriesQueryKey() });
        setEditingId(null);
        editForm.reset();
      },
      onError: () => toast({ title: "Failed to update registry", variant: "destructive" }),
    });
  };

  const handleDelete = (id: number) => {
    if (confirm("Delete this registry? This will also remove all items.")) {
      deleteRegistry.mutate({ id }, {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getListGiftRegistriesQueryKey() }),
        onError: () => toast({ title: "Failed to delete registry", variant: "destructive" }),
      });
    }
  };

  const handleLookup = () => {
    const code = lookupCode.trim().toUpperCase();
    if (!code) return;
    const found = registries?.find(r => r.registryCode === code);
    if (found) {
      setViewingId(found.id);
      setLookupCode("");
    } else {
      toast({ title: "Registry not found", description: `No registry found for code: ${code}`, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gift Registry</h1>
          <p className="text-muted-foreground mt-1">Manage wish lists for weddings, birthdays, and other events.</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => createForm.reset()}>
              <Plus className="w-4 h-4 mr-2" /> New Registry
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Create Gift Registry</DialogTitle></DialogHeader>
            <RegistryForm form={createForm} onSubmit={handleCreate} isPending={createRegistry.isPending} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit modal */}
      <Dialog open={editingId !== null} onOpenChange={o => !o && setEditingId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit Registry</DialogTitle></DialogHeader>
          <RegistryForm form={editForm} onSubmit={handleUpdate} isPending={updateRegistry.isPending} />
        </DialogContent>
      </Dialog>

      {/* View/detail modal */}
      <Dialog open={viewingId !== null} onOpenChange={o => !o && setViewingId(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Registry Details</DialogTitle></DialogHeader>
          {viewingId !== null && <RegistryDetailModal registryId={viewingId} onClose={() => setViewingId(null)} />}
        </DialogContent>
      </Dialog>

      {/* Lookup bar + filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 w-52 bg-background">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            className="text-sm bg-transparent outline-none w-full"
            placeholder="Search registries..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterEvent} onValueChange={setFilterEvent}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All events" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            {EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <Input
            className="w-44 h-9 font-mono text-sm"
            placeholder="Lookup by code..."
            value={lookupCode}
            onChange={e => setLookupCode(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && handleLookup()}
          />
          <Button variant="outline" size="sm" onClick={handleLookup}>
            <Search className="w-4 h-4 mr-1" /> Lookup
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Registrant</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="text-center">Items</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : registries?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-16">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Gift className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                    <div>
                      <p className="font-semibold">No registries yet</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Create a gift registry for a customer's wedding, birthday, or other event.</p>
                    </div>
                    <Button size="sm" className="gap-2 mt-1" onClick={() => setIsCreateOpen(true)}>
                      <Plus className="w-4 h-4" /> Create First Registry
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : registries?.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">
                  {r.registrantName}
                  {r.partnerName ? <span className="text-muted-foreground"> & {r.partnerName}</span> : null}
                </TableCell>
                <TableCell>{r.eventType}</TableCell>
                <TableCell>{r.eventDate ? format(new Date(r.eventDate), "d MMM yyyy") : "—"}</TableCell>
                <TableCell>
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{r.registryCode}</code>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.phone || r.email || "—"}</TableCell>
                <TableCell className="text-center">
                  <span className="text-sm">
                    <span className={r.fulfilledCount === r.itemCount && r.itemCount > 0 ? "text-green-600 font-semibold" : ""}>
                      {r.fulfilledCount}
                    </span>
                    /{r.itemCount}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-xs ${statusColor(r.status)}`}>{r.status}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setViewingId(r.id)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(r)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(r.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
